import _ from 'lodash';
import PropTypes from 'prop-types';
import moment from 'moment';
import {
  roomItem as strings,
  roomDetailDialog as roomDetailDialogStrings,
} from 'ui/constants/strings';

const lightDarkLevel = 100;
const temperatureAlertLevel = 30;
const humidityAlertLevel = 80;
export const minDate = moment.utc('1800-01-01');

export const gender = {
  male: 'male',
  female: 'female',
  undefined: '',
};

export const genderText = {
  male: '男',
  female: '女',
  undefined: '-',
};

export const deviceName = {
  // room status
  presence: 'at_home',
  pirDetect: 'pir_detected',
  door: 'doorsensor_status',
  lightLevel: 'envlux',
  // monitor status
  heartRate: 'heart_rate',
  breath: 'respiratory_rate',
  heartRateOld: 'heartRate',
  breathOld: 'breathRate',
  temperature: 'envtemp',
  humidity: 'envhumid',
  // alert
  sos: 'SOS',
  fall: '跌倒',
};

export const deviceNamePropTypes = PropTypes.oneOf([
  // room status
  deviceName.presence,
  deviceName.pirDetect,
  deviceName.door,
  deviceName.lightLevel,
  // monitor status
  deviceName.heartRate,
  deviceName.breath,
  deviceName.heartRateOld,
  deviceName.breathOld,
  deviceName.temperature,
  deviceName.humidity,
  // alert
  deviceName.sos,
  deviceName.fall,
]);

export const roomType = {
  bedroom: 'bedroom',
  toilet: 'toilet',
};

export const roomTypePropTypes = PropTypes.oneOf([
  roomType.bedroom,
  roomType.toilet,
]);

export const usage = {
  interior: 'interior',
  toilet: 'toilet',
  leave_bed: 'leave_bed',
  door_guard: 'door_guard',
  bed: 'bed',
};

export const isMale = sex => sex === gender.male;
export const isFemale = sex => sex === gender.female;

// const sleepBelt = 'y8ffp9jbdf1je23m';
const sleepBeltOld = 'z400twp-2';

const isOldDataByPid = (pid) => {
  if (pid === sleepBeltOld) {
    return true;
  }
  return false;
};

export const isOldData = devices => _.some(devices, device => isOldDataByPid(device.pid));

const getHeartRateDevice = (devices) => {
  if (isOldData(devices)) {
    return _.find(
      devices,
      device => device.dataField === deviceName.heartRateOld
        && device.usage === usage.bed,
    );
  }
  return _.find(
    devices,
    device => device.dataField === deviceName.heartRate && device.usage === usage.bed,
  );
};

const getBreathDevice = (devices) => {
  if (isOldData(devices)) {
    return _.find(
      devices,
      device => device.dataField === deviceName.breathOld && device.usage === usage.bed,
    );
  }
  return _.find(
    devices,
    device => device.dataField === deviceName.breath && device.usage === usage.bed,
  );
};

export const constructStatusDetail = (devices, alert) => {
  const heartRateDevice = getHeartRateDevice(devices);
  const breathDevice = getBreathDevice(devices);
  const temperatureDevice = _.find(devices, {
    dataField: deviceName.temperature,
    usage: usage.interior,
  });
  const humidityDevice = _.find(devices, {
    dataField: deviceName.humidity,
    usage: usage.interior,
  });

  let heartRateText = strings.deviceNull;
  let breathText = strings.deviceNull;
  let temperatureText = strings.deviceNull;
  let humidityText = strings.deviceNull;
  let botBarStatus = false;

  if (heartRateDevice) {
    heartRateText = _.get(heartRateDevice, 'offline')
      ? strings.deviceOffline
      : _.get(heartRateDevice, 'value', strings.deviceNull);
  }

  if (breathDevice) {
    breathText = _.get(breathDevice, 'offline')
      ? strings.deviceOffline
      : _.get(breathDevice, 'value', strings.deviceNull);
  }

  if (temperatureDevice) {
    const temperature = _.get(temperatureDevice, 'value', strings.deviceNull);
    temperatureText = _.get(temperatureDevice, 'offline')
      ? strings.deviceOffline
      : `${temperature / 10}°C`;
    if (
      temperature !== strings.deviceNull
      && temperature > temperatureAlertLevel
    ) {
      botBarStatus = true;
    }
  }

  if (humidityDevice) {
    const humidity = _.get(humidityDevice, 'value', strings.deviceNull);
    humidityText = _.get(humidityDevice, 'offline')
      ? strings.deviceOffline
      : `${humidity / 10}%`;
    if (humidity !== strings.deviceNull && humidity > humidityAlertLevel) {
      botBarStatus = true;
    }
  }

  const data = {
    iconTL: `iconHeartRate${alert ? 'Inv' : ''}`,
    textTL: heartRateText,
    iconTR: `iconBreath${alert ? 'Inv' : ''}`,
    textTR: breathText,
    iconBL: `iconTemperature${alert || botBarStatus ? 'Inv' : ''}`,
    textBL: temperatureText,
    iconBR: `iconHumidity${alert || botBarStatus ? 'Inv' : ''}`,
    textBR: humidityText,
    botBarStatus,
  };
  return data;
};

const roomStatusIconClass = {
  alert: 'iconAlert',
  bed: 'iconBed',
  man: 'iconMan',
  manMissing: 'iconManDash',
  washroom: 'iconWashroom',
  deviceOffline: 'textDeviceOffline',
};

const isInWashroom = (devices) => {
  const pirDevice = _.find(devices, {
    dataField: deviceName.pirDetect,
    usage: usage.toilet,
  });
  const value = _.get(pirDevice, 'value');
  return JSON.parse(!!value);
};

const isInBed = (devices) => {
  const heartRateDevice = getHeartRateDevice(devices);
  const breathDevice = getBreathDevice(devices);
  const heartRate = _.get(heartRateDevice, 'value', null);
  const breath = _.get(breathDevice, 'value', null);
  return heartRate && heartRate > 0 && breath && breath > 0;
};

const isInDoor = (devices) => {
  const pirDevice = _.find(devices, {
    dataField: deviceName.pirDetect,
    usage: usage.interior,
  });
  const value = _.get(pirDevice, 'value');
  return JSON.parse(!!value);
};

const isAtHomeAvailable = (devices) => {
  const pirDevice = _.find(devices, {
    dataField: deviceName.presence,
  });
  if (_.get(pirDevice, 'available', false)) {
    return true;
  }
  return false;
};

const isAtHomeOnly = (devices) => {
  const pirDevice = _.find(devices, {
    dataField: deviceName.presence,
  });
  const value = _.get(pirDevice, 'value', 0);
  return value === 1;
};

const isAtHome = (devices) => {
  const pirDevice = _.find(devices, {
    dataField: deviceName.presence,
  });
  if (isAtHomeAvailable(devices)) {
    const value = _.get(pirDevice, 'value', 0);
    return value === 1;
  }
  return isInDoor(devices);
};

const isLightOn = (devices) => {
  if (!isInBed(devices) && isAtHome(devices)) {
    const lightDevice = _.find(devices, {
      dataField: deviceName.lightLevel,
      usage: usage.interior,
    });
    // if there is no light device, default value should be true
    if (lightDevice) {
      const lightLevel = _.get(lightDevice, 'value', 0);
      return lightLevel > lightDarkLevel;
    }
  }
  return true;
};

// const isDoorOpen = (devices) => {
//   const doorDevice = _.find(devices, {
//     dataField: deviceName.door, usage: usage.door_guard,
//   });
//   const value = _.get(doorDevice, 'value', 'false');
//   return JSON.parse(value.length > 0 ? value : false);
// };

const getIconBaseOnPIR = (devices) => {
  if (isInWashroom(devices) && isAtHome(devices)) {
    const washroomDevice = _.find(devices, {
      dataField: deviceName.pirDetect,
      usage: usage.toilet,
    });
    const bedroomDevice = _.find(devices, {
      dataField: deviceName.presence,
    });
    if (
      moment(washroomDevice.lastUpdated).isBefore(bedroomDevice.lastUpdated)
    ) {
      return roomStatusIconClass.man;
    }
    return roomStatusIconClass.washroom;
  }
  if (isInWashroom(devices)) {
    return roomStatusIconClass.washroom;
  }
  if (isAtHomeAvailable(devices)) {
    return isAtHomeOnly(devices)
      ? roomStatusIconClass.man
      : roomStatusIconClass.manMissing;
  }
  return isInDoor(devices) ? 'pir_true' : 'pir_false';
};

const isDeviceOffline = (devices) => {
  const filteredArray = _.filter(
    devices,
    device => device.dataField !== deviceName.breath
      && device.dataField !== deviceName.heartRate
      && device.dataField !== deviceName.heartRateOld
      && device.dataField !== deviceName.breathOld,
  );
  return _.some(filteredArray, ['offline', true]);
};

const getRoomStatusIcon = (devices, alert) => {
  if (alert) {
    return roomStatusIconClass.alert;
  }
  if (isDeviceOffline(devices)) {
    return roomStatusIconClass.deviceOffline;
  }
  if (isInBed(devices)) {
    return roomStatusIconClass.bed;
  }
  return getIconBaseOnPIR(devices);
};

export const getRoomMarginRight = (screenWidth, roomWidth) => {
  const numberOfRoom = Math.floor(screenWidth / roomWidth);
  const remainWidth = screenWidth - numberOfRoom * roomWidth;
  const marginRight = Math.floor(remainWidth / numberOfRoom);
  if (marginRight < 8) {
    return Math.floor((remainWidth + roomWidth) / (numberOfRoom - 1));
  }
  return marginRight;
};

export const isAlertOn = (suiteId, alerts) => _.some(alerts, alert => alert.familyId === suiteId);

export const prepNotificationList = (families, alerts) => {
  const fmyObject = _.reduce(
    families,
    (result, obj) => {
      // eslint-disable-next-line no-param-reassign
      result[obj.familyId] = {
        name: obj.name,
        patient: obj.patient,
      };
      return result;
    },
    {},
  );
  const notifications = [];
  _.forEach(alerts, (item) => {
    notifications.push({
      ...item,
      name: fmyObject[item.familyId].name,
      patient: fmyObject[item.familyId].patient,
    });
  });
  return notifications;
};

export const constructRoomDetailStatus = (devices) => {
  const heartRateDevice = getHeartRateDevice(devices);
  const breathDevice = getBreathDevice(devices);
  const temperatureDevice = _.find(devices, {
    dataField: deviceName.temperature,
    usage: usage.interior,
  });
  const humidityDevice = _.find(devices, {
    dataField: deviceName.humidity,
    usage: usage.interior,
  });
  const washroomDevice = _.find(devices, {
    dataField: deviceName.pirDetect,
    usage: usage.toilet,
  });
  const doorDevice = _.find(devices, {
    dataField: deviceName.door,
    usage: usage.door_guard,
  });
  const indoorDevice = _.find(devices, {
    dataField: deviceName.presence,
  });
  const lightDevice = _.find(devices, {
    dataField: deviceName.lightLevel,
    usage: usage.interior,
  });

  let heartRateText = strings.deviceNull;
  let breathText = strings.deviceNull;
  let temperatureText = strings.deviceNull;
  let humidityText = strings.deviceNull;
  let washroomText = strings.deviceNull;
  let doorText = strings.deviceNull;
  let indoorText = strings.deviceNull;
  let lightText = strings.deviceNull;
  let heartRateOffline = false;
  let breathOffline = false;
  let temperatureOffline = false;
  let humidityOffline = false;
  let washroomOffline = false;
  let doorOffline = false;
  let indoorOffline = false;
  let lightOffline = false;

  if (heartRateDevice) {
    if (_.get(heartRateDevice, 'offline')) {
      heartRateOffline = true;
      heartRateText = strings.textDeviceOffline;
    } else {
      heartRateText = _.get(heartRateDevice, 'value', strings.deviceNull);
    }
  }

  if (breathDevice) {
    if (_.get(breathDevice, 'offline')) {
      breathOffline = true;
      breathText = strings.textDeviceOffline;
    } else {
      breathText = _.get(breathDevice, 'value', strings.deviceNull);
    }
  }

  if (temperatureDevice) {
    if (_.get(temperatureDevice, 'offline')) {
      temperatureOffline = true;
      temperatureText = strings.textDeviceOffline;
    } else {
      temperatureText = `${_.get(temperatureDevice, 'value') / 10}°C`;
    }
  }

  if (humidityDevice) {
    if (_.get(humidityDevice, 'offline')) {
      humidityOffline = true;
      humidityText = strings.textDeviceOffline;
    } else {
      humidityText = `${_.get(humidityDevice, 'value') / 10}%`;
    }
  }

  if (washroomDevice) {
    if (_.get(washroomDevice, 'offline')) {
      washroomOffline = true;
      washroomText = strings.textDeviceOffline;
    } else {
      washroomText = isInWashroom(devices)
        ? strings.inWashroom
        : strings.notInWashroom;
    }
  }

  if (doorDevice) {
    if (_.get(doorDevice, 'offline')) {
      doorOffline = true;
      doorText = strings.textDeviceOffline;
    } else {
      doorText = JSON.parse(_.get(doorDevice, 'value'))
        ? strings.doorOpen
        : strings.doorClose;
    }
  }

  if (indoorDevice) {
    if (_.get(indoorDevice, 'offline')) {
      indoorOffline = true;
      indoorText = strings.textDeviceOffline;
    } else {
      indoorText = isInDoor(devices)
        ? "<div class='roomStatusContainer'><div class='roomStatusIcon iconEye'></div><div class='roomStatusIcon iconMan'></div></div>"
        : "<div class='roomStatusContainer'><div class='roomStatusIcon iconEye'></div><div class='roomStatusIcon'></div></div>";
    }
  }

  if (lightDevice) {
    if (_.get(lightDevice, 'offline')) {
      lightOffline = true;
      lightText = strings.textDeviceOffline;
    } else {
      lightText = _.get(lightDevice, 'value', 0) > lightDarkLevel
        ? strings.lightOn
        : strings.lightOff;
    }
  }

  const data = {
    heartRateText,
    breathText,
    washroomText,
    doorText,
    temperatureText,
    humidityText,
    indoorText,
    lightText,
    heartRateOffline,
    breathOffline,
    washroomOffline,
    doorOffline,
    temperatureOffline,
    humidityOffline,
    indoorOffline,
    lightOffline,
  };
  return data;
};

export const getGraphTitle = (selectedRoom) => {
  switch (selectedRoom) {
    case deviceName.heartRate:
    case deviceName.heartRateOld:
      return roomDetailDialogStrings.heartRate;
    case deviceName.breath:
    case deviceName.breathOld:
      return roomDetailDialogStrings.breath;
    case deviceName.pirDetect:
      return roomDetailDialogStrings.washroom;
    case deviceName.door:
      return roomDetailDialogStrings.door;
    default:
      return strings.deviceNull;
  }
};

export const getDeviceData = (devices, selectedRoom) => {
  switch (selectedRoom) {
    case deviceName.heartRate:
    case deviceName.heartRateOld:
      return getHeartRateDevice(devices);
    case deviceName.breath:
    case deviceName.breathOld:
      return getBreathDevice(devices);
    case deviceName.pirDetect:
      return _.find(devices, {
        dataField: deviceName.pirDetect,
        usage: usage.toilet,
      });
    case deviceName.door:
      return _.find(devices, {
        dataField: deviceName.door,
        usage: usage.door_guard,
      });
    default:
      return undefined;
  }
};

const graphSetting = {
  [deviceName.heartRate]: {
    datasets: {
      label: `${roomDetailDialogStrings.heartRate}: `,
      fill: false,
      lineTension: 0,
      backgroundColor: 'white',
      borderColor: '#FF4E89',
      spanGaps: false,
      borderWidth: 1,
      borderCapStyle: 'butt',
      borderDash: [],
      borderDashOffset: 0.0,
      borderJoinStyle: 'miter',
      pointBorderColor: '#FF4E89',
      pointBackgroundColor: '#fff',
      pointBorderWidth: 1,
      pointHoverRadius: 1,
      pointHoverBackgroundColor: '#FF4E89',
      pointHoverBorderColor: '#FF4E89',
      pointHoverBorderWidth: 2,
      pointRadius: 1,
      pointHitRadius: 10,
    },
    options: {
      chartArea: {
        backgroundColor: '#FFFFFF',
      },
      legend: {
        display: false,
      },
      tooltips: {
        mode: 'label',
        callbacks: {
          title() {
            return '';
          },
          beforeLabel(tooltipItem) {
            return `${moment(tooltipItem.xLabel)
              .utcOffset('+09:00')
              .format('hh:mm:ss A')}`;
          },
          label(tooltipItem) {
            return `${roomDetailDialogStrings.heartRate}: ${
              tooltipItem.value < 0
                ? roomDetailDialogStrings.offBed
                : tooltipItem.value
            }`;
          },
        },
      },
      scales: {
        xAxes: [
          {
            type: 'time',
            time: {
              parser: x => moment(
                moment(x).utcOffset('+09:00').format('YYYY-MM-DD HH:mm'),
              ),
            },
          },
        ],
        yAxes: [
          {
            ticks: {
              max: 150,
              min: -5,
              stepSize: 30,
              callback: val => (val < 0 ? roomDetailDialogStrings.offBed : val),
            },
          },
        ],
      },
    },
  },
  [deviceName.heartRateOld]: {
    datasets: {
      label: `${roomDetailDialogStrings.heartRate}: `,
      fill: false,
      lineTension: 0,
      backgroundColor: 'white',
      borderColor: '#FF4E89',
      spanGaps: false,
      borderWidth: 1,
      borderCapStyle: 'butt',
      borderDash: [],
      borderDashOffset: 0.0,
      borderJoinStyle: 'miter',
      pointBorderColor: '#FF4E89',
      pointBackgroundColor: '#fff',
      pointBorderWidth: 1,
      pointHoverRadius: 1,
      pointHoverBackgroundColor: '#FF4E89',
      pointHoverBorderColor: '#FF4E89',
      pointHoverBorderWidth: 2,
      pointRadius: 1,
      pointHitRadius: 10,
    },
    options: {
      chartArea: {
        backgroundColor: '#FFFFFF',
      },
      legend: {
        display: false,
      },
      tooltips: {
        mode: 'label',
        callbacks: {
          title() {
            return '';
          },
          beforeLabel(tooltipItem) {
            return `${moment(tooltipItem.xLabel)
              .utcOffset('+09:00')
              .format('hh:mm:ss A')}`;
          },
          label(tooltipItem) {
            return `${roomDetailDialogStrings.heartRate}: ${
              tooltipItem.value < 0
                ? roomDetailDialogStrings.offBed
                : tooltipItem.value
            }`;
          },
        },
      },
      scales: {
        xAxes: [
          {
            type: 'time',
            time: {
              parser: x => moment(
                moment(x).utcOffset('+09:00').format('YYYY-MM-DD HH:mm'),
              ),
            },
          },
        ],
        yAxes: [
          {
            ticks: {
              max: 150,
              min: -5,
              stepSize: 30,
              callback: val => (val < 0 ? roomDetailDialogStrings.offBed : val),
            },
          },
        ],
      },
    },
  },
  [deviceName.breath]: {
    datasets: {
      label: `${roomDetailDialogStrings.breath}: `,
      fill: false,
      lineTension: 0,
      backgroundColor: 'white',
      borderColor: '#FF4E89',
      spanGaps: false,
      borderWidth: 1,
      borderCapStyle: 'butt',
      borderDash: [],
      borderDashOffset: 0.0,
      borderJoinStyle: 'miter',
      pointBorderColor: '#FF4E89',
      pointBackgroundColor: '#fff',
      pointBorderWidth: 1,
      pointHoverRadius: 1,
      pointHoverBackgroundColor: '#FF4E89',
      pointHoverBorderColor: '#FF4E89',
      pointHoverBorderWidth: 2,
      pointRadius: 1,
      pointHitRadius: 10,
    },
    options: {
      chartArea: {
        backgroundColor: '#FFFFFF',
      },
      legend: {
        display: false,
      },
      tooltips: {
        callbacks: {
          title() {
            return '';
          },
          beforeLabel(tooltipItem) {
            return `${moment(tooltipItem.xLabel)
              .utcOffset('+09:00')
              .format('hh:mm:ss A')}`;
          },
          label(tooltipItem) {
            return `${roomDetailDialogStrings.breath}: ${
              tooltipItem.value < 0
                ? roomDetailDialogStrings.offBed
                : tooltipItem.value
            }`;
          },
        },
      },
      scales: {
        xAxes: [
          {
            type: 'time',
            time: {
              parser: x => moment(
                moment(x).utcOffset('+09:00').format('YYYY-MM-DD HH:mm'),
              ),
            },
          },
        ],
        yAxes: [
          {
            ticks: {
              max: 30,
              min: -1,
              callback: val => (val < 0 ? roomDetailDialogStrings.offBed : val),
            },
          },
        ],
      },
    },
  },
  [deviceName.breathOld]: {
    datasets: {
      label: `${roomDetailDialogStrings.breath}: `,
      fill: false,
      lineTension: 0,
      backgroundColor: 'white',
      borderColor: '#FF4E89',
      spanGaps: false,
      borderWidth: 1,
      borderCapStyle: 'butt',
      borderDash: [],
      borderDashOffset: 0.0,
      borderJoinStyle: 'miter',
      pointBorderColor: '#FF4E89',
      pointBackgroundColor: '#fff',
      pointBorderWidth: 1,
      pointHoverRadius: 1,
      pointHoverBackgroundColor: '#FF4E89',
      pointHoverBorderColor: '#FF4E89',
      pointHoverBorderWidth: 2,
      pointRadius: 1,
      pointHitRadius: 10,
    },
    options: {
      chartArea: {
        backgroundColor: '#FFFFFF',
      },
      legend: {
        display: false,
      },
      tooltips: {
        callbacks: {
          title() {
            return '';
          },
          beforeLabel(tooltipItem) {
            return `${moment(tooltipItem.xLabel)
              .utcOffset('+09:00')
              .format('hh:mm:ss A')}`;
          },
          label(tooltipItem) {
            return `${roomDetailDialogStrings.breath}: ${
              tooltipItem.value < 0
                ? roomDetailDialogStrings.offBed
                : tooltipItem.value
            }`;
          },
        },
      },
      scales: {
        xAxes: [
          {
            type: 'time',
            time: {
              parser: x => moment(
                moment(x).utcOffset('+09:00').format('YYYY-MM-DD HH:mm'),
              ),
            },
          },
        ],
        yAxes: [
          {
            ticks: {
              max: 30,
              min: -1,
              stepSize: 6,
              callback: val => (val < 0 ? roomDetailDialogStrings.offBed : val),
            },
          },
        ],
      },
    },
  },
  [deviceName.pirDetect]: {
    datasets: {
      label: `${roomDetailDialogStrings.washroom}: `,
      fill: false,
      lineTension: 0,
      backgroundColor: 'white',
      borderColor: '#FF4E89',
      spanGaps: false,
      borderWidth: 1,
      borderCapStyle: 'butt',
      borderDash: [],
      borderDashOffset: 0.0,
      borderJoinStyle: 'miter',
      pointBorderColor: '#FF4E89',
      pointBackgroundColor: '#FF4E89',
      pointBorderWidth: 3,
      pointHoverRadius: 1,
      pointHoverBackgroundColor: '#FF4E89',
      pointHoverBorderColor: '#FF4E89',
      pointHoverBorderWidth: 2,
      pointRadius: 3,
      pointHitRadius: 10,
    },
    options: {
      chartArea: {
        backgroundColor: '#FFFFFF',
      },
      legend: {
        display: false,
      },
      tooltips: {
        callbacks: {
          label: (tooltipItem, data) => {
            let label = data.datasets[tooltipItem.datasetIndex].label || '';
            label += moment(tooltipItem.xLabel)
              .utcOffset('+09:00')
              .format('hh:mm:ss A');
            return label;
          },
        },
      },
    },
  },
  [deviceName.door]: {
    datasets: {
      label: `${roomDetailDialogStrings.door}: `,
      fill: false,
      lineTension: 0,
      backgroundColor: 'white',
      borderColor: '#FF4E89',
      spanGaps: false,
      borderWidth: 1,
      borderCapStyle: 'butt',
      borderDash: [],
      borderDashOffset: 0.0,
      borderJoinStyle: 'miter',
      pointBorderColor: '#FF4E89',
      pointBackgroundColor: '#FF4E89',
      pointBorderWidth: 3,
      pointHoverRadius: 1,
      pointHoverBackgroundColor: '#FF4E89',
      pointHoverBorderColor: '#FF4E89',
      pointHoverBorderWidth: 2,
      pointRadius: 3,
      pointHitRadius: 10,
    },
    options: {
      chartArea: {
        backgroundColor: '#FFFFFF',
      },
      legend: {
        display: false,
      },
      tooltips: {
        callbacks: {
          label: (tooltipItem, data) => {
            let label = data.datasets[tooltipItem.datasetIndex].label || '';

            label += moment(tooltipItem.xLabel)
              .utcOffset('+09:00')
              .format('hh:mm:ss A');
            return label;
          },
        },
      },
    },
  },
};

const generateLabels = (date) => {
  const dateCopy = moment(date);
  const lables = [dateCopy.format('M/D')];
  let i;
  for (i = 0; i < 6; i += 1) {
    lables.push(dateCopy.subtract(1, 'days').format('M/D'));
  }
  return lables;
};

export const constructGraphData = selectedTile => graphSetting[selectedTile];
export const constructFinalData = (
  data,
  selectedTile,
  selectedDate,
  graphData,
) => {
  const dataX = {
    datasets: [
      {
        ...graphData.datasets,
        data,
      },
    ],
  };
  switch (selectedTile) {
    case deviceName.heartRate:
    case deviceName.breath:
    case deviceName.heartRateOld:
    case deviceName.breathOld:
      return {
        data: dataX,
        options: graphData.options,
      };
    case deviceName.pirDetect:
    case deviceName.door:
      // eslint-disable-next-line no-case-declarations
      const scales = {
        xAxes: [
          {
            type: 'time',
            time: {
              parser: (x) => {
                const temp = moment(x)
                  .utcOffset('+09:00')
                  .format('HH:mm:ss.SSS');
                return moment(temp, 'HH:mm:ss.SSS');
              },
            },
          },
        ],
        yAxes: [
          {
            type: 'category',
            labels: generateLabels(selectedDate),
          },
        ],
      };
      return {
        data: dataX,
        options: {
          ...graphData.options,
          scales,
        },
      };
    default:
      return null;
  }
};

export const lineChartPlugin = {
  beforeDraw(chart) {
    if (
      chart.config.options.chartArea
      && chart.config.options.chartArea.backgroundColor
    ) {
      const { ctx } = chart.chart;
      const { chartArea } = chart;
      // var values = chart.data.datasets[0].data; // Added
      // var columnCount = chart.data.datasets[0].data.length;
      const rowCount = 5; // Replace by the number of rows you need
      const width = chartArea.right - chartArea.left;
      const height = chartArea.bottom - chartArea.top;

      // var columnWidth = width/columnCount;
      const rowHeight = height / rowCount;
      ctx.save();
      ctx.fillStyle = chart.config.options.chartArea.backgroundColor;
      let startPoint = chartArea.top;
      while (startPoint < chartArea.bottom) {
        ctx.fillRect(chartArea.left, startPoint, width, rowHeight);
        startPoint += rowHeight * 2;
      }
      ctx.restore();
    }
  },
};

export const scatterChartPlugin = {
  beforeDraw(chart) {
    if (
      chart.config.options.chartArea
      && chart.config.options.chartArea.backgroundColor
    ) {
      const { ctx } = chart.chart;
      const { chartArea } = chart;
      // var values = chart.data.datasets[0].data; // Added
      // var columnCount = chart.data.datasets[0].data.length;
      const rowCount = 6; // Replace by the number of rows you need
      const width = chartArea.right - chartArea.left;
      const height = chartArea.bottom - chartArea.top;

      // var columnWidth = width/columnCount;
      const rowHeight = height / rowCount;
      ctx.save();
      ctx.fillStyle = chart.config.options.chartArea.backgroundColor;
      let startPoint = chartArea.top;
      while (startPoint < chartArea.bottom) {
        ctx.fillRect(chartArea.left, startPoint, width, rowHeight);
        startPoint += rowHeight * 2;
      }
      ctx.restore();
    }
  },
};

export default {
  gender,
  roomStatusIconClass,
  isMale,
  isFemale,
  isLightOn,
  deviceName,
  deviceNamePropTypes,
  roomType,
  roomTypePropTypes,
  getRoomStatusIcon,
  constructStatusDetail,
};
